import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowRight, Loader2, CheckCircle2, Circle, Clock, Bug, Layers, Lock } from "lucide-react";
import { getStoredUser, getAuthConfig, loginWithGoogle } from "../lib/userAuth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PythonLabModulePage() {
  const { moduleSlug } = useParams();
  const navigate = useNavigate();
  const user = getStoredUser();
  const [loading, setLoading] = useState(true);
  const [module, setModule] = useState(null);
  const [progress, setProgress] = useState({ lessons_completed: 0, progress_pct: 0 });
  const [lessonProgress, setLessonProgress] = useState({});
  const [lockedReason, setLockedReason] = useState(null); // { prevTitle, prevSlug }

  useEffect(() => {
    if (!user?.session_token) {
      loginWithGoogle(`/ai-tools/python-lab/module/${moduleSlug}`);
      return;
    }
    setLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const modRes = await axios.get(`${API}/python-lab/modules/${moduleSlug}`);
        if (cancelled) return;
        setModule(modRes.data);

        const moduleId = modRes.data?.module_id;
        const moduleOrder = modRes.data?.order || 0;
        const total = (modRes.data?.lessons || []).length;

        const [aggRes, lessonsRes] = await Promise.all([
          axios.get(`${API}/python-lab/me/progress`, getAuthConfig()).catch(() => ({ data: [] })),
          moduleId
            ? axios
                .get(`${API}/python-lab/me/progress/lessons`, {
                  ...getAuthConfig(),
                  params: { module_id: moduleId }
                })
                .catch(() => ({ data: {} }))
            : Promise.resolve({ data: {} })
        ]);
        if (cancelled) return;

        // Prerequisite gate: any earlier module not at 100% blocks access
        const allModules = (aggRes.data || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
        const blocker = allModules.find(
          (m) => (m.order || 0) < moduleOrder && (m.progress_pct || 0) < 100
        );
        if (blocker) {
          setLockedReason({ prevTitle: blocker.title, prevSlug: blocker.slug });
          setLoading(false);
          return;
        }

        const lessonMap = lessonsRes.data || {};
        setLessonProgress(lessonMap);

        const completedCount = Object.values(lessonMap).filter(
          (p) => p?.status === "completed"
        ).length;
        const target = allModules.find((m) => m.module_id === moduleId);
        setProgress({
          lessons_completed: completedCount || target?.lessons_completed || 0,
          progress_pct: total
            ? Math.round((completedCount / total) * 100)
            : target?.progress_pct || 0
        });
      } catch (e) {
        console.error("Module load failed:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [moduleSlug, user?.session_token]);

  if (!user?.session_token) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 size={28} className="text-[#00F0FF] animate-spin" />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 size={28} className="text-[#00F0FF] animate-spin" />
      </div>
    );
  }

  if (!module) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center p-6">
        <p className="text-zinc-400 font-dm-sans mb-4">Module not found</p>
        <button onClick={() => navigate("/ai-tools/python-lab")} className="text-[#00F0FF] font-dm-sans">← Back to Python Lab</button>
      </div>
    );
  }

  if (lockedReason) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="max-w-md w-full text-center bg-zinc-900/60 border border-zinc-800 rounded-2xl p-10"
          data-testid="module-locked-screen"
        >
          <div className="w-14 h-14 rounded-full bg-zinc-800/80 border border-zinc-700 flex items-center justify-center mx-auto mb-5">
            <Lock size={22} className="text-zinc-300" />
          </div>
          <h1 className="font-outfit font-black text-2xl text-white mb-2">Module locked</h1>
          <p className="text-sm text-zinc-400 font-dm-sans leading-relaxed mb-7">
            Complete every lesson in <span className="text-white font-bold">{lockedReason.prevTitle}</span> to unlock <span className="text-white font-bold">{module.title}</span>.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <button
              data-testid="locked-go-prev-module"
              onClick={() => navigate(`/ai-tools/python-lab/module/${lockedReason.prevSlug}`)}
              className="bg-[#00F0FF] text-black font-outfit font-bold px-5 py-2.5 rounded-lg hover:bg-white transition-colors inline-flex items-center justify-center gap-2"
            >
              Continue {lockedReason.prevTitle} <ArrowRight size={14} />
            </button>
            <button
              data-testid="locked-back-to-lab"
              onClick={() => navigate("/ai-tools/python-lab")}
              className="bg-zinc-800 text-white font-dm-sans px-5 py-2.5 rounded-lg hover:bg-zinc-700 transition-colors"
            >
              Back to Python Lab
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  const lessons = module.lessons || [];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-zinc-900 sticky top-0 z-30 bg-[#050505]/85 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            data-testid="back-to-lab"
            onClick={() => navigate("/ai-tools/python-lab")}
            className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm font-dm-sans transition-colors"
          >
            <ArrowLeft size={16} /> Python Lab
          </button>
          <div className="text-xs text-zinc-500 font-dm-sans">
            {progress.lessons_completed}/{lessons.length} done · {progress.progress_pct}%
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10"
        >
          <span className="text-[10px] font-mono font-bold text-[#00F0FF] bg-[#00F0FF]/10 px-2 py-0.5 rounded">
            MODULE {String(module.order || 0).padStart(2, "0")}
          </span>
          <h1 className="font-outfit font-black text-4xl mt-3 text-white">{module.title}</h1>
          <p className="text-zinc-400 mt-3 max-w-3xl font-dm-sans">{module.summary}</p>
          <div className="flex items-center gap-5 mt-5 text-xs text-zinc-500 font-dm-sans">
            <span className="inline-flex items-center gap-1.5">
              <Clock size={12} /> ~{module.estimated_minutes} min
            </span>
            <span>{lessons.length} lessons</span>
          </div>
        </motion.div>

        <div className="space-y-2">
          {lessons.map((l, idx) => {
            const lp = lessonProgress[l.lesson_id];
            const isDone = lp?.status === "completed";
            const isInProgress = lp?.status === "in_progress";
            return (
              <motion.button
                key={l.lesson_id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: idx * 0.04 }}
                onClick={() => navigate(`/ai-tools/python-lab/lesson/${l.slug}`)}
                data-testid={`lesson-${l.slug}`}
                data-completed={isDone ? "true" : "false"}
                className={`w-full ${
                  isDone
                    ? "bg-emerald-500/[0.04] border-emerald-500/30 hover:border-emerald-400/60"
                    : "bg-zinc-900/60 border-zinc-800 hover:border-[#00F0FF]/40 hover:bg-zinc-900"
                } border rounded-xl p-5 flex items-center gap-5 transition-all text-left group`}
              >
                <span className={`font-mono text-sm w-8 ${isDone ? "text-emerald-400/70" : "text-zinc-600"}`}>
                  {String(l.order || idx + 1).padStart(2, "0")}
                </span>
                {isDone ? (
                  <CheckCircle2
                    size={20}
                    className="text-emerald-400 shrink-0"
                    data-testid={`lesson-status-${l.slug}-completed`}
                  />
                ) : (
                  <Circle
                    size={20}
                    className={`shrink-0 transition-colors ${
                      isInProgress
                        ? "text-amber-400"
                        : "text-zinc-700 group-hover:text-[#00F0FF]"
                    }`}
                    data-testid={`lesson-status-${l.slug}-${isInProgress ? "in-progress" : "not-started"}`}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className={`font-outfit font-bold text-base ${isDone ? "text-white/90" : "text-white"}`}>
                      {l.title}
                    </div>
                    {isDone && (
                      <span className="inline-flex items-center gap-1 text-[9px] font-mono font-bold text-emerald-400 bg-emerald-400/10 border border-emerald-400/30 px-1.5 py-0.5 rounded">
                        <CheckCircle2 size={9} /> COMPLETED
                      </span>
                    )}
                    {!isDone && isInProgress && (
                      <span className="inline-flex items-center gap-1 text-[9px] font-mono font-bold text-amber-400 bg-amber-400/10 border border-amber-400/30 px-1.5 py-0.5 rounded">
                        IN PROGRESS
                      </span>
                    )}
                    {l.kind === "debug" && (
                      <span className="inline-flex items-center gap-1 text-[9px] font-mono font-bold text-amber-400 bg-amber-400/10 border border-amber-400/30 px-1.5 py-0.5 rounded">
                        <Bug size={9} /> {(l.bug_category || '').toUpperCase()}
                      </span>
                    )}
                    {l.kind === "project_step" && (
                      <span className="inline-flex items-center gap-1 text-[9px] font-mono font-bold text-purple-400 bg-purple-400/10 border border-purple-400/30 px-1.5 py-0.5 rounded">
                        <Layers size={9} /> STEP {l.step_number}
                      </span>
                    )}
                  </div>
                  {l.tags?.length > 0 && (
                    <div className="flex items-center gap-2 mt-1.5">
                      {l.tags.slice(0, 3).map((t) => (
                        <span key={t} className="text-[10px] font-dm-sans text-zinc-500 bg-zinc-800 px-1.5 py-0.5 rounded">{t}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className={`text-xs font-dm-sans flex items-center gap-1 ${isDone ? "text-emerald-400/80" : "text-zinc-500"}`}>
                  +{l.xp_reward || 10} XP
                </div>
                <ArrowRight size={16} className={`transition-all ${isDone ? "text-emerald-400/60" : "text-zinc-600 group-hover:text-[#00F0FF] group-hover:translate-x-1"}`} />
              </motion.button>
            );
          })}
        </div>
      </main>
    </div>
  );
}
